import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input, output, signal } from '@angular/core';

import { SettingsStore } from '@core/settings';
import { BadgeComponent } from '@shared/components/badge/badge.component';
import { BadgeVariant } from '@shared/components/badge/badge.model';
import { ItemAssetType } from '@features/player/data-access/player.config';
import { SkinningService } from '@core/skin';
import { TranslocoPipe } from '@jsverse/transloco';
import { TranscodeImageDirective } from '@shared/directives/transcode-image.directive';

import { IPromoCatalogueDto } from '@features/assets/data-access/catalogues-api.dto';
import { ImageType } from '@features/assets/components/asset-card/asset-list-item.types';

type PromoImageFormat = 'landscape' | 'portrait' | 'square' | 'circle';

const enum CountdownDisplayType {
    None = 1,
    Standard = 2,
    Big = 3,
}

export interface IPromoOpenedEvent {
    readonly referenceId: string;
    readonly promo: IPromoCatalogueDto;
}

@Component({
    selector: 'asset-promo-catalogue',
    standalone: true,
    imports: [BadgeComponent, TranslocoPipe, TranscodeImageDirective],
    templateUrl: './asset-promo-catalogue.html',
    styleUrl: './asset-promo-catalogue.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssetPromoCatalogue implements OnInit {
    private readonly settings = inject(SettingsStore);
    protected readonly skinning = inject(SkinningService);

    readonly promo = input.required<IPromoCatalogueDto | null>();
    readonly referenceId = input.required<string>();

    readonly opened = output<IPromoOpenedEvent>();

    protected readonly BadgeVariant = BadgeVariant;

    readonly imageFormat = computed<PromoImageFormat>(() => {
        switch (this.promo()?.promoImageTypeId) {
            case ImageType.Landscape:      return 'landscape';
            case ImageType.Portrait:       return 'portrait';
            case ImageType.Square:         return 'square';
            case ImageType.Circle:         return 'circle';
            case ImageType.TileImage:      return 'landscape';
            case ImageType.Background:     return 'landscape';
            // New tile formats share the base promo shape (the promo image has no
            // separate small/large sizing — its dimensions come from the layout).
            case ImageType.SmallPortrait:  return 'portrait';
            case ImageType.LargeLandscape: return 'landscape';
            default:                       return 'landscape';
        }
    });

    readonly isAvailabilityStartPassed = signal<boolean | null>(null);

    // A Big countdown that is still counting down to its (future) start time.
    readonly isBigCountdownActive = computed(() => {
        const block = this.promo()?.promoBlock;
        if (!block) return false;
        return block.countdownDisplayType === CountdownDisplayType.Big
            && !!block.availabilityStartTime
            && this.isAvailabilityStartPassed() !== true;
    });

    readonly showBadge = computed(() => {
        const block = this.promo()?.promoBlock;
        if (!block) return false;
        // hideBadge hides every badge and countdown, EXCEPT a still-running Big countdown:
        // in that case the manual badge is suppressed and the Big countdown is shown instead.
        // Once the countdown ends (its start time has passed) nothing should show.
        if (this.promo()?.hideBadge === true) return this.isBigCountdownActive();
        const hasRegular = block.badge !== null && block.badge !== undefined;
        const hasCountdown = !!block.availabilityStartTime
            && block.countdownDisplayType !== CountdownDisplayType.None;
        return hasRegular || hasCountdown;
    });

    readonly badgeVariant = computed<BadgeVariant>(() => {
        const block = this.promo()?.promoBlock;
        if (!block) return BadgeVariant.PromoNormal;
        // When hideBadge is set, an active Big countdown takes over and suppresses the manual badge.
        if (this.promo()?.hideBadge === true && this.isBigCountdownActive()) return BadgeVariant.PromoBig;
        if (block.badge) return BadgeVariant.PromoNormal;
        if (block.countdownDisplayType === CountdownDisplayType.Big) return BadgeVariant.PromoBig;
        return BadgeVariant.PromoNormal;
    });

    readonly isEpisodeType = computed(
        () => this.promo()?.promoBlock?.promotedItemType === ItemAssetType.Episode,
    );

    readonly showPromoButton = computed(
        () => this.settings.showPromoButtonReview() && !!this.promo()?.hasItems,
    );

    readonly promoFrontOutlineVisible = computed(
        () => this.settings.promoFrontOutlineVisible(),
    );

    ngOnInit(): void {
        const startTime = this.promo()?.promoBlock?.availabilityStartTime;
        if (startTime) {
            const startMs = new Date(startTime).getTime();
            if (!isNaN(startMs)) {
                this.isAvailabilityStartPassed.set(startMs <= Date.now());
            }
        }
    }

    onAvailabilityStartPassed(passed: boolean): void {
        this.isAvailabilityStartPassed.set(passed);
    }

    onClick(): void {
        const promo = this.promo();
        if (!promo?.hasItems) return;
        this.opened.emit({ referenceId: this.referenceId(), promo });
    }
}
